import { t } from 'i18next'

import { getIsNetwork } from '~/helpers'

import { FormattedDestinationUrl } from '../../helpers/formatDestinationUrls'
import DestinationUrlRow from '../DestinationUrlRow'
import SubpublisherDestinationSection, {
  SubpublisherDestinationUrlEntry
} from '../SubpublisherDestinationSection'

export type SubpublisherDestination = {
  publisherId: PublisherId
  publisherName: string
  campaignStatus?: 'waiting' | 'approved' | 'rejected'
  rejectionReason?: string
  destinationUrls: SubpublisherDestinationUrlEntry[]
}

const DestinationUrlsContent = ({
  campaignId,
  showSubpublisherSections,
  subpublishers,
  groupBrands = false,
  awaitingDistribution = false,
  formattedDestinationUrls,
  getName,
  onSubpublisherSaved
}: {
  campaignId: CampaignId
  showSubpublisherSections: boolean
  subpublishers: SubpublisherDestination[]
  groupBrands?: boolean
  awaitingDistribution?: boolean
  formattedDestinationUrls?: FormattedDestinationUrl[]
  getName: (publisherId: string, brand: string) => string | undefined
  onSubpublisherSaved: () => void
}) => {
  if (showSubpublisherSections) {
    const isNetwork = getIsNetwork()
    const compareNames = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    const renderSubpublisher = (
      subpublisher: SubpublisherDestination,
      layoutMode: 'grouped' | 'flat',
      visibleBrand?: string | null
    ) => (
      <SubpublisherDestinationSection
        key={`${subpublisher.publisherId}-${visibleBrand ?? 'publisher'}`}
        campaignId={campaignId}
        publisherId={subpublisher.publisherId}
        publisherName={subpublisher.publisherName}
        rejectionReason={subpublisher.rejectionReason}
        destinationUrls={subpublisher.destinationUrls}
        layoutMode={layoutMode}
        visibleBrand={visibleBrand}
        getName={getName}
        canEdit={isNetwork && subpublisher.campaignStatus === 'approved'}
        blockedStatus={
          subpublisher.campaignStatus === 'waiting' ||
          subpublisher.campaignStatus === 'rejected'
            ? subpublisher.campaignStatus
            : undefined
        }
        statusPending={awaitingDistribution && !subpublisher.campaignStatus}
        onSaved={onSubpublisherSaved}
      />
    )

    if (groupBrands) {
      return [...subpublishers]
        .sort((a, b) => compareNames(a.publisherName, b.publisherName))
        .map(subpublisher =>
          renderSubpublisher(
            {
              ...subpublisher,
              destinationUrls: [...subpublisher.destinationUrls].sort((a, b) =>
                compareNames(
                  a.name || a.brand || subpublisher.publisherName,
                  b.name || b.brand || subpublisher.publisherName
                )
              )
            },
            'grouped'
          )
        )
    }

    return subpublishers
      .flatMap(subpublisher =>
        subpublisher.destinationUrls.map(destinationUrl => ({
          subpublisher,
          destinationUrl
        }))
      )
      .sort((a, b) =>
        compareNames(
          a.destinationUrl.name ||
            a.destinationUrl.brand ||
            a.subpublisher.publisherName,
          b.destinationUrl.name ||
            b.destinationUrl.brand ||
            b.subpublisher.publisherName
        )
      )
      .map(({ subpublisher, destinationUrl }) =>
        renderSubpublisher(subpublisher, 'flat', destinationUrl.brand ?? null)
      )
  }

  if (!formattedDestinationUrls?.length) {
    return <>{t('rm:noUrlsRegistered')}</>
  }

  return formattedDestinationUrls.map(item => (
    <DestinationUrlRow
      key={`${item.publisher_id}-${item.code ?? ''}`}
      url={item.url}
      name={item.name}
    />
  ))
}

export default DestinationUrlsContent
